import type { EAnalyticMode } from '~/modules/analytics/types';
import type { IExtraCondition } from '~/core/gemxql/types';

import { useControlSettingStore } from '~/presentation/reports/common/ReportFilter/stores';
import { EControlSettingType, EReportControlId } from '~/presentation/reports/common/type';
import { getControlIdsById, getControlValueById } from '~/presentation/reports/common';
import { useOrderControlStore as useSdkOrderControlStore } from '@gem-sdk/analytics';
import { useOrderDateTime } from '~/presentation/reports/ui/SelectDate/hooks';
import { EComparisonOperator, EGroupOperator } from '~/core/gemxql/types';
import { buildOrderQuery } from '../../utils/order-query';
import { QUERY_KEY } from '~/presentation/reports/query';
import { useOrderControlStore } from '../../stores';
import { useSearchParams } from '@remix-run/react';
import { useEffect, useMemo } from 'react';

interface UseOrderParametersProps {
  mode: EAnalyticMode;
  metrics: string[];
  isQueryTotal?: boolean;
  isLoading?: boolean;
  customFilters?: Record<string, any>;
  isSyncSearchParams?: boolean;
  limit?: number;
  offset?: number;
  versionIds?: Record<string, any>;
  queryValue?: string;
}

export const useOrderParameters = ({
  mode,
  metrics,
  isQueryTotal = false,
  isLoading = false,
  customFilters,
  isSyncSearchParams = true,
  limit,
  offset,
  versionIds,
  queryValue,
}: UseOrderParametersProps) => {
  const orderControlData = useOrderControlStore((state) => state.orderControlData);
  const orderBy = useSdkOrderControlStore((state) => state.orderBy);
  const controlSettingUI = useControlSettingStore((state) => state.controlSettingUI);

  const hasCampaignFilter = !!orderControlData?.[EReportControlId.TAB_CAMPAIGN]?.value;
  const { currentDates } = useOrderDateTime({ hasCampaignFilter, isQueryTotal });

  const [searchParams, setSearchParams] = useSearchParams();

  const searchCondition = useMemo<IExtraCondition | undefined>(() => {
    const trimmed = queryValue?.trim();
    if (!trimmed) return undefined;
    return {
      operator: EComparisonOperator.GROUP,
      groupOperator: EGroupOperator.OR,
      conditions: [
        { operator: EComparisonOperator.LIKE, fields: ['order_id'], value: trimmed },
        { operator: EComparisonOperator.EQ, fields: ['gx_id'], value: trimmed },
      ],
    };
  }, [queryValue]);

  const query = useMemo(() => {
    const orderControlSettingUI = controlSettingUI[EControlSettingType.ORDER];
    const filterIds = getControlIdsById(orderControlSettingUI, EReportControlId.FILTER);
    const filters = customFilters ?? getControlValueById(filterIds, orderControlData);
    const extraConditions = searchCondition ? [searchCondition] : undefined;

    return buildOrderQuery({
      mode,
      sources: ['sales'],
      metrics,
      dimensions: [],
      dateRange: currentDates,
      filters: !isQueryTotal ? filters : {},
      orderBy: orderBy ? { [orderBy.field]: orderBy } : undefined,
      limit,
      offset,
      versionIds,
      extraConditions: !isQueryTotal ? extraConditions : undefined,
    });
  }, [
    controlSettingUI,
    currentDates,
    customFilters,
    isQueryTotal,
    limit,
    offset,
    metrics,
    mode,
    orderBy,
    orderControlData,
    versionIds,
    searchCondition,
  ]);

  useEffect(() => {
    if (!query || !isSyncSearchParams) return;

    const newParams = new URLSearchParams(searchParams);
    newParams.set(QUERY_KEY, JSON.stringify(query));

    setSearchParams(newParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isSyncSearchParams]);

  if (isLoading) return { parameters: { input: '' } };

  return {
    parameters: { input: query },
  };
};
